import type { StaffAnalyticsOverview } from "@lafayette/schemas";

import { moneyText, monthText } from "../format";
import { barLayout, gridValues, niceMax } from "./chart-math";

// "Spend by month" (Paper): one bar for each month with a payment, 1 px grid lines, palette colours. The chart is decoration (`aria-hidden`):
// each month, its amount and its payments are written as a list under it.

type Month = StaffAnalyticsOverview["records"]["spendByMonth"][number];

const WIDTH = 360;
const HEIGHT = 120;

export function SpendChart({ months }: Readonly<{ months: readonly Month[] }>) {
  const max = niceMax(Math.max(...months.map((month) => month.amountMinor), 0));
  const bars = barLayout(
    months.map((month) => month.amountMinor),
    { width: WIDTH, height: HEIGHT },
    max,
    0.4,
  );
  return (
    <div className="grid gap-2">
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox={`0 0 ${WIDTH} ${HEIGHT + 18}`}
        className="h-auto w-full"
      >
        {gridValues(max).map((value) => {
          const y = HEIGHT - (value / max) * HEIGHT;
          return (
            <line
              key={value}
              x1={0}
              x2={WIDTH}
              y1={y}
              y2={y}
              className="stroke-border"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
        {bars.map((bar, index) => {
          const month = months[index] as Month;
          return (
            <g key={month.month}>
              <rect
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                rx={Math.min(5, bar.width / 2)}
                className={index === bars.length - 1 ? "fill-primary" : "fill-accent"}
              />
              <text
                x={bar.x + bar.width / 2}
                y={HEIGHT + 14}
                textAnchor="middle"
                className="fill-muted-foreground text-[10px]"
              >
                {monthText(month.month, true)}
              </text>
            </g>
          );
        })}
      </svg>
      <ul className="m-0 grid list-none gap-0.5 p-0 text-sm">
        {months.map((month) => (
          <li key={month.month} className="flex items-baseline justify-between gap-3">
            <span className="text-muted-foreground">{monthText(month.month)}</span>
            <span>
              <span className="font-serif font-medium">{moneyText(month.amountMinor)}</span>{" "}
              <span className="text-muted-foreground">
                · {month.payments} {month.payments === 1 ? "payment" : "payments"}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
